import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  Loader2, 
  ChevronRight, 
  ShoppingCart, 
  Zap, 
  Maximize2,
  CheckCircle2,
  ArrowRight,
  ShieldCheck,
  Cpu,
  Waves
} from 'lucide-react';

import Header from '../../Components/Header';
import Footer from '../../Components/Footer';

const Products = () => {
  const location = useLocation();
  
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCatId, setSelectedCatId] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prodLoading, setProdLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Hardcoded premium data to supplement the UI
  const premiumInnovations = [
    { title: "Eco-Active Hydraulics", desc: "Pressure-flow compensation reducing thermal loss.", icon: <Waves size={16}/> },
    { title: "Eco-Torque", desc: "Reduced carbon footprint with optimized fuel consumption.", icon: <Maximize2 size={16}/> },
    { title: "Safety+ Guard", desc: "Reinforced load sensors with automatic emergency braking.", icon: <ShieldCheck size={16}/> }
  ];

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/bookings/categories`);
        setCategories(res.data);
        const initialId = location.state?.categoryId || res.data[0]?._id;       
        setSelectedCatId(initialId);
      } catch (err) {
        console.error("Error fetching categories", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, [location.state]);

  useEffect(() => {
    document.title = "Marvel Sato | Products";

    if (selectedCatId) {
      const fetchProducts = async () => {
        setProdLoading(true);
        try {
          const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/bookings/products/${selectedCatId}`);
          setProducts(res.data);
          setSelectedProduct(res.data[0] || null);
        } catch (err) {
          console.error("Error fetching products", err);
        } finally {
          setProdLoading(false);
        }
      };
      fetchProducts();
    }
  }, [selectedCatId]);

  if (loading) return (
    <div className="h-screen w-full flex flex-col items-center justify-center bg-white">
      <Loader2 className="animate-spin text-orange-500 mb-4" size={32} />
      <p className="text-slate-400 font-bold tracking-widest uppercase text-[10px]">Syncing Fleet Data...</p>
    </div>
  );

  return (
    <div className="bg-white min-h-screen font-sans selection:bg-orange-500 selection:text-white">
      <Header />

      {/* --- SECTION 1: COMPACT CATEGORY SELECTOR --- */}
      <section className="pt-24 md:pt-32 pb-4 bg-slate-900 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-6 flex items-end justify-between">
            <div>
              <h2 className="text-orange-500 text-[9px] font-black uppercase tracking-[0.4em] mb-1">Fleet Selection</h2>
              <h1 className="text-xl md:text-2xl font-black text-white italic uppercase tracking-tighter leading-none">
                Choose <span className="text-orange-500 not-italic">Series</span>
              </h1>
            </div>
            <div className="hidden md:flex items-center gap-2 text-slate-500 text-[9px] uppercase font-bold tracking-widest pb-1">
              Scroll <ArrowRight size={12} />
            </div>
          </div>
          
          <div className="flex gap-2 md:gap-3 overflow-x-auto pb-4 no-scrollbar snap-x">
            {categories.map((cat, index) => (
              <motion.div
                key={cat._id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setSelectedCatId(cat._id)}
                className={`min-w-[140px] md:min-w-[180px] snap-center cursor-pointer group relative p-3 md:p-4 rounded-xl border transition-all duration-500 ${
                  selectedCatId === cat._id 
                  ? 'border-orange-500 bg-orange-500/5 shadow-lg shadow-orange-500/5' 
                  : 'border-slate-800 bg-slate-800/20 hover:border-slate-700'
                }`}
              >
                <div className="relative z-10">
                  <h3 className={`text-[10px] md:text-xs font-black uppercase italic transition-colors leading-tight ${selectedCatId === cat._id ? 'text-orange-500' : 'text-slate-500 group-hover:text-white'}`}>
                    {cat.categoryName} 
                  </h3>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* --- SECTION 2: PRODUCT EXPLORER --- */}
      <div className="max-w-7xl mx-auto px-6 py-8 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          
          {/* SIDEBAR: MODEL SELECTION */}
          <aside className="lg:col-span-3">
            <div className="sticky top-32">
              <p className="text-slate-400 text-[9px] font-black uppercase tracking-[0.3em] mb-6">Series Models</p>
              <div className="space-y-1.5">
                {products.map((prod) => (
                  <button
                    key={prod._id}
                    onClick={() => setSelectedProduct(prod)}
                    className={`w-full group flex items-center justify-between px-4 py-3 rounded-lg border transition-all duration-300 ${
                      selectedProduct?._id === prod._id 
                      ? 'border-orange-500 bg-white shadow-md' 
                      : 'border-slate-100 bg-white hover:border-slate-300'
                    }`}
                  >
                    <span className={`text-[10px] md:text-[11px] font-black uppercase tracking-tight ${selectedProduct?._id === prod._id ? 'text-slate-900' : 'text-slate-400'}`}>
                      {prod.productName}
                    </span>
                    <ChevronRight size={14} className={selectedProduct?._id === prod._id ? 'text-orange-500' : 'text-slate-200'} />
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* MAIN DISPLAY */}
          <main className="lg:col-span-9">
            <AnimatePresence mode="wait">
              {prodLoading ? (
                <div className="h-96 flex items-center justify-center"><Loader2 className="animate-spin text-slate-100" size={32} /></div>
              ) : selectedProduct ? (
                <motion.div 
                  key={selectedProduct._id}
                  initial={{ opacity: 0, x: 20 }} 
                  animate={{ opacity: 1, x: 0 }} 
                  exit={{ opacity: 0, x: -20 }} 
                  className="space-y-12"
                >
                  
                  {/* TOP PRODUCT INFO */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                    <div 
                      className="relative aspect-square bg-slate-50 rounded-[2rem] overflow-hidden shadow-xl group border-8 border-white"
                      onMouseEnter={() => setIsHovered(true)}
                      onMouseLeave={() => setIsHovered(false)}
                    >
                      <img 
                        src={isHovered ? selectedProduct.productImage2 : selectedProduct.productImage1} 
                        alt={selectedProduct.productName}
                        className="w-full h-full object-cover transition-transform duration-1000 scale-100 group-hover:scale-105"
                      />
                      <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest flex items-center gap-2 shadow-sm">
                        <Maximize2 size={12} /> {isHovered ? 'Alt Perspective' : 'Core View'}
                      </div>
                    </div>

                    <div className="flex flex-col">
                      <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 uppercase italic tracking-tighter leading-[0.85] mb-8">
                        {selectedProduct.productName}
                      </h2>
                      
                      <div className="grid grid-cols-2 gap-3 mb-8">
                        {selectedProduct.subFeatures?.map((spec, i) => (
                          <div 
                            key={i} 
                            className="bg-slate-50 p-4 rounded-xl border border-slate-100 hover:border-orange-100 transition-colors"
                          >
                            <p className="text-[8px] text-orange-500 font-black uppercase tracking-widest mb-1">{spec.title}</p>
                            <p className="text-xs md:text-sm font-black text-slate-900 leading-tight">{spec.description}</p>
                          </div>
                        ))}
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3">
                        <Link to="/book" state={{ product: selectedProduct }} className="flex-1">
                          <button className="w-full bg-slate-900 text-white py-4 rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-orange-500 transition-all flex items-center justify-center gap-2 shadow-lg shadow-slate-200">
                            Book Now <ShoppingCart size={14} /> 
                          </button> 
                        </Link>
                        <Link to="/interested" state={{ product: selectedProduct }} className="flex-1">
                          <button className="w-full border-2 border-slate-900 text-slate-900 py-4 rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-50 transition-all">
                            Send Inquiry
                          </button>
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* BOTTOM: TECHNICAL DATA (REORDERED) */}
                  <div className="space-y-12">
                    
                    {/* 1. MAIN FEATURES & SYSTEMS (NOW TOP) */}
                    <div className="space-y-6">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="text-orange-500" size={18} />
                        <h4 className="text-slate-900 text-[11px] font-black uppercase tracking-widest italic">Core Performance Systems</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {selectedProduct.mainFeatures?.map((feature, i) => (
                          <div key={i} className="p-5 bg-white rounded-xl border border-slate-100 hover:border-orange-500/20 hover:shadow-sm transition-all">
                            <p className="text-orange-500 font-black uppercase text-[8px] mb-2 tracking-widest">{feature.title}</p>
                            <p className="text-slate-500 text-[10px] md:text-[11px] font-bold leading-relaxed uppercase">{feature.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* 2. KEY INNOVATION GRID */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch pt-12 border-t border-slate-100">
                      {/* PRIMARY DATA CARD */}
                      <div className="lg:col-span-5">
                        <div className="h-full bg-slate-900 text-white p-8 rounded-[2rem] shadow-xl flex flex-col">
                          <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center mb-6">
                             <Zap className="text-white" size={20} />
                          </div>
                          <h3 className="text-xl font-black uppercase italic mb-4">The Innovation</h3>
                          <p className="text-slate-400 text-xs md:text-sm leading-relaxed font-medium italic mb-8">
                            "{selectedProduct.keyFeatures}"
                          </p>
                          
                          {/* HARDCODED SUPPLEMENTARY SPECS */}
                          <div className="mt-auto pt-6 border-t border-white/10 grid grid-cols-2 gap-4">
                             <div>
                                <p className="text-orange-500 text-[8px] font-black uppercase">Built-To-Last</p>
                                <p className="text-[10px] text-white/60 font-medium">High Precision</p>
                             </div>
                             <div>
                                <p className="text-orange-500 text-[8px] font-black uppercase">Support</p>
                                <p className="text-[10px] text-white/60 font-medium">24/7 Global Fleet</p>
                             </div>
                          </div>
                        </div>
                      </div>

                      {/* SUPPLEMENTARY INNOVATIONS */}
                      <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {premiumInnovations.map((item, idx) => (
                          <div key={idx} className="p-6 bg-slate-50 rounded-[1.5rem] border border-slate-100 hover:bg-white transition-all group">
                             <div className="text-orange-500 mb-4 group-hover:scale-110 transition-transform">{item.icon}</div>
                             <h5 className="text-[10px] font-black text-slate-900 uppercase tracking-tighter mb-1">{item.title}</h5>
                             <p className="text-[10px] text-slate-400 font-medium leading-tight">{item.desc}</p>
                          </div>
                        ))}
                        <div className="p-6 bg-orange-500 rounded-[1.5rem] flex flex-col justify-center items-center text-center">
                           <h5 className="text-[10px] font-black text-white uppercase tracking-tighter">Certified Safety</h5>
                           <p className="text-[9px] text-white/80 font-medium mt-1 uppercase italic tracking-widest">ISO 9001:2026</p>
                        </div>
                      </div>
                    </div>

                  </div>

                </motion.div>
              ) : null}
            </AnimatePresence>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Products;